/**
 * agentModelsStore — per-agent model list cache.
 *
 * Backs the model dropdown in agent options. Each agent's model list is
 * cached for 24h to avoid hitting `opencode models` (which round-trips to
 * models.dev) on every render. A refresh button on the dropdown forces a
 * re-pull.
 *
 * The Rust side (commands::list_agent_models) returns an empty list for
 * agents that don't expose a stable list command — the UI falls back to
 * the free-form customFlags input in that case.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getBackend } from '@/services/backend';
import { logger } from '@/lib/logger';

export interface AgentModel {
  /** Full --model arg (e.g. `anthropic/claude-sonnet-4-5`). */
  id: string;
  /** Provider stem — what we collapse models under in the dropdown. */
  provider: string;
  /** Provider-stripped model id — the visible label inside each group. */
  name: string;
}

interface AgentModelsCache {
  models: AgentModel[];
  fetchedAt: number;
  /** Sticky error from the most recent failed fetch — surfaced in the
   *  dropdown's empty state so the user knows why the list is empty. */
  error?: string;
}

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

interface AgentModelsState {
  /** Per-agent-type cache. Keyed by AgentType ('opencode', etc.). */
  cache: Record<string, AgentModelsCache | undefined>;
  /** Per-agent-type in-flight promise so concurrent renders share one fetch. */
  inFlight: Record<string, Promise<AgentModel[]> | undefined>;

  /** Get the current models for an agent. Triggers a fetch if cache is
   *  cold or expired; resolves immediately with cached data otherwise. */
  list: (agentType: string, forceRefresh?: boolean) => Promise<AgentModel[]>;
  /** Read cached entry without triggering a fetch — for sync UI access. */
  peek: (agentType: string) => AgentModelsCache | undefined;
  /** Drop the cache for one agent (force the next list() to re-fetch). */
  invalidate: (agentType: string) => void;
}

export const useAgentModelsStore = create<AgentModelsState>()(
  persist(
    (set, get) => ({
      cache: {},
      inFlight: {},

      list: async (agentType, forceRefresh = false) => {
        const existing = get().cache[agentType];
        const fresh = existing && Date.now() - existing.fetchedAt < CACHE_TTL_MS;
        if (existing && fresh && !forceRefresh) return existing.models;

        // Coalesce concurrent renders onto a single fetch — the Rust call
        // can take seconds on a cold opencode/models.dev cache.
        const pending = get().inFlight[agentType];
        if (pending && !forceRefresh) return pending;

        const fetchPromise = (async (): Promise<AgentModel[]> => {
          try {
            const models = await getBackend().system.listAgentModels(agentType);
            set((s) => ({
              cache: { ...s.cache, [agentType]: { models, fetchedAt: Date.now() } },
            }));
            return models;
          } catch (e) {
            const err = e instanceof Error ? e.message : String(e);
            logger.warn('AgentModelsStore', `list_agent_models(${agentType}) failed`, e);
            // Cache the error too so the dropdown can show "couldn't fetch"
            // instead of looking like the agent has no models. Reuse the
            // last good list if we have one.
            set((s) => ({
              cache: {
                ...s.cache,
                [agentType]: {
                  models: existing?.models ?? [],
                  fetchedAt: Date.now(),
                  error: err,
                },
              },
            }));
            return existing?.models ?? [];
          } finally {
            set((s) => ({
              inFlight: { ...s.inFlight, [agentType]: undefined },
            }));
          }
        })();

        set((s) => ({
          inFlight: { ...s.inFlight, [agentType]: fetchPromise },
        }));
        return fetchPromise;
      },

      peek: (agentType) => get().cache[agentType],

      invalidate: (agentType) =>
        set((s) => ({
          cache: { ...s.cache, [agentType]: undefined },
        })),
    }),
    {
      name: 'ateliercode-agent-models',
      partialize: (state) => ({ cache: state.cache }),
    },
  ),
);
